import cn from "classnames";
import { format } from "date-fns/esm";
import React from "react";

import { AccountType, Platform, UserQuestionnaireQuestionType } from "@Core/constants/ENUMS";

import { UserQuestionnaireQuestionInstance } from "./models";

export const getAPIUrl = (): string | undefined => {
  const branch = process.env.REACT_APP_HOST_ENV;

  switch (branch) {
    case "master":
      return process.env.REACT_APP_API_URL_PRODUCTION;
    case "staging":
      return process.env.REACT_APP_API_URL_STAGING;
    case "develop":
      return process.env.REACT_APP_API_URL_DEVELOP;
  }

  return process.env.REACT_APP_API_URL;
};

export const getMediaUrl = (): string | undefined => {
  const branch = process.env.REACT_APP_HOST_ENV;

  switch (branch) {
    case "master":
      return process.env.REACT_APP_MEDIA_URL_PRODUCTION;
    case "staging":
      return process.env.REACT_APP_MEDIA_URL_STAGING;
    case "develop":
      return process.env.REACT_APP_MEDIA_URL_DEVELOP;
  }

  return process.env.REACT_APP_MEDIA_URL;
};

export const formatDate = (date: string): string => {
  const d = new Date(date);
  return format(d, "dd-LL-yyyy").toString();
};

export const getImage = (uri: string): string => {
  return `${getMediaUrl()}${uri}`;
};

export const capitalize = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const leadingZero = (value: string, zeros = 5): string => {
  while (value.length < zeros) value = "0" + value;
  return value;
};

export const getPlatform = (role?: AccountType): Platform => {
  const pathname = window.location.hostname;

  const lni = ["mijn.leiderschapeninzetbaarheid.nl", "staging.lni.sbdev.nl", "dev.lni.sbdev.nl", "lni.sbdev.nl"];
  const lniRoles = [AccountType.User];

  const oom = ["mijn.organisatieadviesopmaat.nl", "staging.oom.sbdev.nl", "dev.oom.sbdev.nl", "oom.sbdev.nl"];
  const oomRoles = [AccountType.Advisor];

  const pni = ["professionaleninzetbaarheid.nl", "staging.pni.sbdev.nl", "dev.pni.sbdev.nl", "pni.sbdev.nl"];
  const pniRoles = [AccountType.Pob, AccountType.HR, AccountType.CompanyDoctor, AccountType.AccountManager];

  if (role) {
    switch (true) {
      case lniRoles.includes(role):
        return Platform.LNI;
      case oomRoles.includes(role):
        return Platform.OOM;
      case pniRoles.includes(role):
        return Platform.PNI;
    }
  }

  switch (true) {
    case lni.includes(pathname):
      return Platform.LNI;
    case oom.includes(pathname):
      return Platform.OOM;
    case pni.includes(pathname):
      return Platform.PNI;
  }

  const item = localStorage.getItem("REACT_APP_MODULE") as Platform;

  return item ? Platform[item] : Platform.LNI;
};

export const nl2br = (content: string): JSX.Element => {
  return <>{content.split("\n").map((str, key) => str.trim().length > 0 && <p key={key}>{str}</p>)}</>;
};

export const timeout = (ms: number): Promise<boolean> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const isProduction = (): boolean => {
  return process.env.REACT_APP_HOST_ENV === "master";
};

export const parseHTML = (html?: string | null, className?: string): JSX.Element => {
  if (!!html) {
    return (
      <div
        className={cn(["HTML", className])}
        dangerouslySetInnerHTML={{
          __html: html,
        }}
      />
    );
  }

  return <></>;
};

export const stripHTML = (html: string) => {
  return html.replace(/(<([^>]+)>)/gi, "");
};

export const openLink = (href: string, target = "_blank"): void => {
  const link = document.createElement("a");
  link.href = href;
  link.target = target;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const getQuestionnaireAnswers = (question?: UserQuestionnaireQuestionInstance | null): string[] => {
  if (question) {
    switch (question.type) {
      case UserQuestionnaireQuestionType.Single:
      case UserQuestionnaireQuestionType.Multiple:
      case UserQuestionnaireQuestionType.SingleDropdown:
      case UserQuestionnaireQuestionType.Statement:
      case UserQuestionnaireQuestionType.Scale:
      case UserQuestionnaireQuestionType.ScaleEmoji:
      case UserQuestionnaireQuestionType.ScaleSlider:
        return question.answers
          .filter((answer) => answer.selected === true)
          .map((item) => {
            if (item.open_input && item.user_answer) {
              return `${item.text} "${item.user_answer}"`;
            }

            return item.text || "-";
          });
      default:
        return [question.user_answer || "-"];
    }
  }

  return ["-"];
};

export const setMatomoUser = (companyId: number, userId: number): void => {
  console.log("Matomo: setCustomDimension", { companyId, userId });

  window._paq.push(["setCustomDimension", 1, companyId]);
  window._paq.push(["setCustomDimension", 2, userId]);
};

export const deleteMatomoUser = (): void => {
  console.log("Matomo: deleteCustomDimension");

  window._paq.push(["deleteCustomDimension", 1]);
  window._paq.push(["deleteCustomDimension", 2]);
};
